import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  FileCheck,
  FileText,
  FormInput,
  LayoutDashboard,
  Upload,
  Users
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: ('role_user' | 'role_admin' | 'role_superadmin')[];
}

const userNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    roles: ['role_user'],
  },
  {
    title: 'Tax Form',
    href: '/tax-form',
    icon: FormInput,
    roles: ['role_user'],
  },
  {
    title: 'My Documents',
    href: '/documents',
    icon: FileText,
    roles: ['role_user'],
  },
  {
    title: 'Tax Filings',
    href: '/tax-filings',
    icon: FileCheck,
    roles: ['role_user'],
  },
  {
    title: 'Upload',
    href: '/upload',
    icon: Upload,
    roles: ['role_user'],
  },
];

const adminNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
    roles: ['role_admin', 'role_superadmin'],
  },
  {
    title: 'Users',
    href: '/admin/users',
    icon: Users,
    roles: ['role_admin', 'role_superadmin'],
  },
  // {
  //   title: 'Documents',
  //   href: '/admin/documents',
  //   icon: FileText,
  //   roles: ['role_admin', 'role_superadmin'],
  // },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { hasRole, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = isAdmin() ? adminNavItems : userNavItems;

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.some(role => hasRole(role));
  });

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Logo area for mobile sheet: dark-logo for light theme, logo for dark theme */}
      <div className="flex h-14 items-center border-b px-4 md:hidden">
        <div className="flex items-center gap-2">
          <img src="/dark-logo.png" alt="CenTax Filer" className="h-8 w-auto dark:hidden" />
          <img src="/logo.png" alt="CenTax Filer" className="h-8 w-auto hidden dark:block" />
          <span className="font-semibold">CenTax Filer</span>
        </div>
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="flex flex-col gap-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/admin' && item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive 
                    ? "bg-accent text-accent-foreground" 
                    : "text-muted-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.title}
                {/* {isActive && <ChevronRight className="ml-auto h-4 w-4" />} */}
              </NavLink>
            );
          })}
        </nav>

        <Separator className="my-4" />

        {/* <nav className="flex flex-col gap-1">
          <NavLink
            to={isAdmin() ? '/admin/settings' : '/settings'}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              (isAdmin() ? location.pathname === '/admin/settings' : location.pathname === '/settings')
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground"
            )}
          >
            <Settings className="h-4 w-4" />
            Settings
          </NavLink>
        </nav> */}
      </ScrollArea>
    </div>
  );
}
